import { CreateResourceAtom } from 'atoms/create-resource-atom';
import { CacheMapEntry } from 'lib/atoms';
import { isNil } from 'lodash-es';
import {
  SlotNRI,
  SlotNRIResponse,
  SlotNRITract,
  TitleQuality,
  TitleRiskFactor,
} from 'types/api-responses';
import { TractNRI, SubtractNRI } from 'components/nri-table/types';
import { format0, formatNumber } from 'utils/number';

// Severity maps mirror AssetRiskFactor::QUALITY_SEVERITY / RISK_SEVERITY on the
// backend — higher index = worse.
const QUALITY_SEVERITY: Record<string, number> = {
  none: 0,
  high: 1,
  medium: 2,
  low: 3,
};
const RISK_SEVERITY: Record<string, number> = {
  none: 0,
  low: 1,
  medium: 2,
  high: 3,
};

// Eval3 enhanced_slot_allocations: nested typeCurveVerification → tract row fields for the table.
const typeCurveVerificationFieldsForSlot = (
  slot: Pick<SlotNRI, 'typeCurveVerification'>
) => {
  const v = slot.typeCurveVerification;
  return {
    typeCurveVerificationStatus: v?.verificationStatus ?? undefined,
    typeCurveLastVerifiedBy: v?.lastVerifiedBy ?? undefined,
    typeCurveLastVerifiedDate: v?.lastVerifiedDate ?? undefined,
  };
};

/**
 * Eval3 enhanced_slot_allocations: the slot's unit source, scenario areas (with the DPS area's
 * href, which the Unvalued Reason links an unscheduled slot to) and well attributes, the same on a
 * tract row (mapSlotNRIResponseToTractNRI) and a slot row (mapSlotNRIResponseToSlotNRI).
 *
 * @param {SlotNRI} slot - The slot from the response.
 * @return {Partial<TractNRI>} Those fields, unset ones as undefined so CellRenderer shows '--'.
 */
const slotAttributeFields = (slot: SlotNRI) => ({
  unitSource: slot.unitTract?.unitSource ?? '--',
  unitDevelopmentType: slot.unitTract?.unitDevelopmentType ?? '--',
  // Scenario-area selections (present in enhanced_slot_allocations response).
  // Left as undefined when unset so CellRenderer shows the '--' placeholder.
  dpsArea: slot.dpsArea ?? undefined,
  dpsAreaHref: slot.dpsAreaHref ?? undefined,
  taxArea: slot.taxArea ?? undefined,
  differentialArea: slot.differentialArea ?? undefined,
  discountRateArea: slot.discountRateArea ?? undefined,
  expenseArea: slot.expenseArea ?? undefined,
  wellName: slot.wellName,
  wellStatus: slot.wellStatus,
  typeCurveStatus: slot.typeCurveStatus ?? '--',
  operator: slot.currentOperator ?? undefined,
  holeDirection: slot.holeDirection ?? undefined,
  formation: slot.formationName,
  basin: slot.basin ?? undefined,
  tier: slot.tier ?? undefined,
  typeCurveAreaName: slot.typeCurveAreaName ?? undefined,
  ...typeCurveVerificationFieldsForSlot(slot),
});

/**
 * Eval3 enhanced_slot_allocations: the slot's dates, volume source, AFE costs and valuation, the
 * same on a tract row (mapSlotNRIResponseToTractNRI) and a slot row (mapSlotNRIResponseToSlotNRI).
 *
 * @param {SlotNRI} slot - The slot from the response.
 * @return {Partial<TractNRI>} Those fields, unset ones as undefined so CellRenderer shows '--'.
 */
const slotDetailFields = (slot: SlotNRI) => ({
  permitDate: slot.permitDate ?? undefined,
  spudDate: slot.spudDate ?? undefined,
  rigReleaseDate: slot.rigReleaseDate ?? undefined,
  completionDate: slot.completionDate ?? undefined,
  firstProductionDate: slot.firstProductionDate ?? undefined,
  trueVerticalDepthFeet: slot.trueVerticalDepthFeet ?? undefined,
  // Volume source fields (present in enhanced_slot_allocations response)
  volumeSourceId: slot.volumeSourceId ?? undefined,
  volumeSourceType: slot.volumeSourceType ?? undefined,
  volumeSourceHref: slot.volumeSourceHref ?? undefined,
  forecastStatus: slot.forecastStatus ?? undefined,
  forecastDate: slot.forecastDate ?? undefined,
  dcaScenario: slot.dcaScenario ?? undefined,
  slotLength: slot.slotLength ?? undefined,
  typeCurveLateralLength: slot.typeCurveLateralLength ?? undefined,
  // AFE cost fields (resolved by backend in enhanced_slot_allocations response)
  afeSourceId: slot.afeSourceId ?? undefined,
  afeSourceHref: slot.afeSourceHref ?? undefined,
  afeWellName: slot.afeWellName ?? undefined,
  slotDrillingCosts: slot.slotDrillingCosts ?? undefined,
  drillingCostsDate: slot.drillingCostsDate ?? undefined,
  slotCompletionCosts: slot.slotCompletionCosts ?? undefined,
  completionCostsDate: slot.completionCostsDate ?? undefined,
  slotFacilitiesCosts: slot.slotFacilitiesCosts ?? undefined,
  facilitiesCostsDate: slot.facilitiesCostsDate ?? undefined,
  slotContingenciesCosts: slot.slotContingenciesCosts ?? undefined,
  contingenciesCostsDate: slot.contingenciesCostsDate ?? undefined,
  slotOtherCosts: slot.slotOtherCosts ?? undefined,
  otherCostsDate: slot.otherCostsDate ?? undefined,
  valued: slot.valued ?? undefined,
  unvaluedReason: slot.unvaluedReason ?? undefined,
  unvaluedReasons: slot.unvaluedReasons ?? undefined,
  landedWithoutFormation: slot.landedWithoutFormation ?? undefined,
  landingInterval: slot.landingInterval ?? undefined,
});

// The certified-source provenance shown beside the interest value (the pill and
// its workspace link). Slot-level on both tables, so the Slots table and the
// Slot Allocations table stay in parity.
const certifiedSourceFields = (
  slot: Pick<
    SlotNRI,
    | 'wiSource'
    | 'nriCertifiedSource'
    | 'nriCertifiedWorkspaceId'
    | 'interestEntityId'
  >
) => ({
  wiSource: slot.wiSource ?? undefined,
  nriCertifiedSource: slot.nriCertifiedSource ?? undefined,
  nriCertifiedWorkspaceId: slot.nriCertifiedWorkspaceId ?? undefined,
  interestEntityId: slot.interestEntityId ?? undefined,
});

// Slot-grain interest, for the Slots table: one row per slot, so the slot-level
// value (which the certified-interest overlay writes) IS the row's value.
const slotInterestFields = (
  slot: Pick<
    SlotNRI,
    | 'nri'
    | 'workingInterest'
    | 'wiSource'
    | 'nriCertifiedSource'
    | 'nriCertifiedWorkspaceId'
    | 'interestEntityId'
  >
) => ({
  totalSlotNri: slot.nri,
  totalSlotWi: slot.workingInterest,
  ...certifiedSourceFields(slot),
});

// Tract-grain interest, for the Slot Allocations table: one row per slot x tract,
// so each row shows THAT tract's share. Mirrors the BE rule
// (InterestCertification::TractInterest) exactly, so the table and the export
// report a certified slot identically (INS-11417):
//
//   1. the tract's own certified share — tested with `!== undefined`, never
//      falsiness, because a certified 0 (a held or uncertified tract on a
//      certified slot) is a real value that must not fall through;
//   2. the slot-level certified scalar, when a certified slot carries no share.
//      This is the ONLY remaining slot-grain path and it is a last resort: a
//      cached calc blob baked before the per-tract overlay. Still certified,
//      never live AM, so a stale blob cannot re-print the original bug — but it
//      DOES repeat the scalar per row, so such rows N-count if summed. Every
//      live producer now distributes per tract, so this should not fire outside
//      historical blobs;
//   3. the tract's live Allocation-Manager allocation, when the slot is not
//      certified.
//
// Since each certified subtract belongs to exactly one tract, (1) sums back to
// the slot total — so the rows reconcile to the Slots table instead of repeating
// the slot value N times and N-counting on a pivot.
const tractInterestFields = (
  slot: Pick<
    SlotNRI,
    | 'nri'
    | 'workingInterest'
    | 'wiSource'
    | 'nriCertifiedSource'
    | 'nriCertifiedWorkspaceId'
    | 'interestEntityId'
  >,
  tract:
    | Pick<
        SlotNRITract,
        'nri' | 'workingInterest' | 'certifiedNri' | 'certifiedWi'
      >
    | undefined
) => {
  const certified = slot.wiSource === 'certified';
  return {
    totalSlotNri:
      tract?.certifiedNri !== undefined
        ? tract.certifiedNri
        : certified
          ? slot.nri
          : tract?.nri,
    totalSlotWi:
      tract?.certifiedWi !== undefined
        ? tract.certifiedWi
        : certified
          ? slot.workingInterest
          : tract?.workingInterest,
    ...certifiedSourceFields(slot),
  };
};

// Picks the worst (most severe) subtract-level risk value across a tract's
// subtracts. The BE query ships risk data per-subtract (INS-8335); the
// Slot Allocations table displays one row per slot+tract, so we collapse
// subtract-level values into a tract-level worst-case for display.
const worstSubtractRisk = <Field extends 'titleQuality' | 'titleRiskFactor'>(
  tract: SlotNRITract | undefined,
  field: Field,
  severity: Record<string, number>
) => {
  const values = (tract?.subtracts ?? [])
    .map((s) => s[field] ?? null)
    .filter(
      (v): v is NonNullable<SlotNRITract['subtracts'][number][Field]> =>
        v !== null
    );
  if (values.length === 0) return undefined;
  return values.reduce((worst, current) => {
    return (severity[current] ?? 0) > (severity[worst] ?? 0) ? current : worst;
  });
};

export const formatMutationResource = <T, U>(
  resource: CreateResourceAtom<T, U>
) => {
  const error =
    resource.result?.type === 'success'
      ? undefined
      : {
          type: resource.result?.type || '',
          message:
            resource.result?.type === 'error'
              ? resource.result.data || []
              : [resource.result?.message || ''],
        };

  return {
    loading: resource.loading,
    error,
    data:
      resource.result?.type === 'success' ? resource.result.data : undefined,
  };
};

export const formatResource = <T>(resource: CacheMapEntry<T>) => {
  let error: string | undefined = undefined;
  let data: T | undefined = undefined;
  if (
    resource.data &&
    typeof resource.data === 'object' &&
    'error' in resource.data
  ) {
    error = resource.data?.error;
  } else {
    data = resource.data;
  }
  return {
    loading: resource.loading,
    error,
    data,
  };
};

export const mapSlotNRIResponseToTractNRI = (
  response: SlotNRIResponse
): TractNRI[] => {
  return response.slots.flatMap((slot) =>
    (slot.tracts ?? []).map((tract) => ({
      geojson: tract.geojson,
      slotNumber: slot.slotNumber,
      crmTract: tract.name,
      crmTractSalesforceId: tract.salesforceId,
      crmAccount: tract.crmAccount?.name ?? '',
      crmAccountHref: tract.crmAccount?.salesforceHref ?? undefined,
      slotId: slot.uwi ?? slot.wellName,
      slotIdHref: slot.href ?? undefined,
      unit: slot.unitTract?.name ?? '--',
      unitHref: slot.unitTract?.href,
      unitTags: slot.unitTags,
      crmTractTags: tract.crmTractTags,
      ...tractInterestFields(slot, tract),
      allocationMethod: slot.allocationMethod,
      allocationManagerId: slot.allocationManagerId,
      devAreaId: slot.unitTract?.id,
      wellId: slot.uwi,
      tractNetAcres: tract.tractNetAcres ?? tract.netAcres,
      netAcreageUnits: tract.netAcreageUnits,
      tractGrossAcres: tract.tractGrossAcres ?? tract.grossAcres,
      surveyArea: tract.surveyArea,
      stateAbbreviation: slot.stateAbbreviation ?? undefined,
      quarterCall: tract.quarterCall,
      instrumentType: tract.instrumentType,
      interestType: tract.interestType ?? slot.interestType ?? undefined,
      depthRange: tract.depthRange
        ? `${formatNumber(
            tract.depthRange.startDepth ?? 0,
            0,
            0,
            'decimal',
            true
          )} - ${
            tract.depthRange.endDepth !== null &&
            tract.depthRange.endDepth !== undefined
              ? formatNumber(tract.depthRange.endDepth, 0, 0, 'decimal', true)
              : '∞'
          }`
        : undefined,
      ...slotAttributeFields(slot),
      ...slotDetailFields(slot),
      targetNpvExcluded: tract.targetNpvExcluded ?? undefined,
      maxNpvExcluded: tract.maxNpvExcluded ?? undefined,
      targetNpvIncluded: tract.targetNpvIncluded ?? undefined,
      maxNpvIncluded: tract.maxNpvIncluded ?? undefined,
      subtracts: tract.subtracts.map((subtract): SubtractNRI => {
        // Convert SlotNRITractNriFormula to TractNRIFormulaData format
        // Handle both new API format and legacy format
        const formula = subtract.tractNriFormula;
        const tractNriFormula = formula
          ? {
              // New API format uses leaseRoyaltyRate, legacy uses royaltyRate
              royaltyRate:
                formula.leaseRoyaltyRate ??
                (formula.royaltyRate ? parseFloat(formula.royaltyRate) : 0),
              // New API format uses nma, legacy uses netMineralAcres
              netMineralAcres:
                formula.nma ??
                (formula.netMineralAcres
                  ? parseFloat(formula.netMineralAcres)
                  : 0),
              // New API format uses grossAcres, legacy uses totalTractAcres
              totalTractAcres:
                formula.grossAcres ??
                (formula.totalTractAcres
                  ? parseFloat(formula.totalTractAcres)
                  : 0),
              // New API format has nra directly, otherwise calculate from NMA
              netRoyaltyAcres:
                formula.nra ??
                (tract.netAcreageUnits === 'nra' &&
                (formula.nma || formula.netMineralAcres) &&
                (formula.leaseRoyaltyRate || formula.royaltyRate)
                  ? ((formula.nma ??
                      parseFloat(formula.netMineralAcres ?? '0')) *
                      8) /
                    (formula.leaseRoyaltyRate ??
                      parseFloat(formula.royaltyRate ?? '0'))
                  : undefined),
              // Instrument type for formula display
              instrumentType: formula.instrumentType,
              // ORRI Burdens for OGL formula display
              orriBurdens: formula.orriBurdens ?? 0,
            }
          : null;

        // Determine calculation method from tract's netAcreageUnits
        const calculationMethod: 'nra' | 'nma' =
          tract.netAcreageUnits?.toLowerCase() === 'nma' ? 'nma' : 'nra';

        const uat = subtract.unitAllocationTract;
        let allocationValue: number | undefined;
        let allocationValueUnit: string | undefined;
        if (uat?.allocationValue) {
          const match = uat.allocationValue.match(/^([\d.,]+)\s*(.*)$/);
          if (match) {
            allocationValue = parseFloat(match[1].replace(/,/g, ''));
            allocationValueUnit = match[2] || undefined;
          }
        }
        if (allocationValue === undefined && !isNil(uat?.gisAcres)) {
          allocationValue = uat.gisAcres;
          allocationValueUnit = 'ac';
        }

        return {
          nriCalculationId: subtract.nriCalculationId,
          verifications: subtract.verifications,
          crmSubtractId: subtract.name,
          crmSubtractSalesforceId: subtract.salesforceId,
          crmTractSalesforceId: tract.salesforceId,
          uat: subtract.unitAllocationTract?.tractName,
          uatNri: subtract.nri,
          uatWi: subtract.workingInterest,
          nriFormula: subtract.nriFormula,
          tractNriFormula,
          calculationMethod,
          slotName: slot.wellName ?? slot.uwi ?? undefined, // For NRI Matrix popup
          subtractNetAcres: subtract.netAcres,
          subtractGrossAcres: subtract.grossAcres,
          quarterCall: subtract.quarterCall,
          partTractFactor: subtract.parameters.participationTractFactor,
          leaseRoyaltyRate: subtract.bundleOfSticks?.leaseRoyaltyRate,
          leaseRoyaltyType: subtract.bundleOfSticks?.leaseRoyaltyType,
          unitCoverage: subtract.parameters.unitCoverageThreshold,
          uatCoverage: subtract.parameters.uatCoverageThreshold,
          executive: subtract.bundleOfSticks?.executive,
          bonus: subtract.bundleOfSticks?.bonus,
          delay: subtract.bundleOfSticks?.delay,
          royalty: subtract.bundleOfSticks?.royalty,
          royaltyBurdens: subtract.bundleOfSticks?.royaltyBurdens,
          wi: subtract.bundleOfSticks?.wi,
          orri: subtract.bundleOfSticks?.orri,
          orriBurdens: subtract.bundleOfSticks?.orriBurdens,
          allocationValue,
          allocationValueUnit,
          allocationFactor: uat?.allocationFactor,
          geojson: subtract.geojson,
          intersectionGeojson: subtract.intersectionGeojson,
        };
      }),
    }))
  );
};

export const mapSlotNRIResponseToSlotNRI = (
  response: SlotNRIResponse
): TractNRI[] => {
  return response.slots.map((slot) => {
    // Get first tract for CRM tract/account info (if available).
    // The flat /entities/:id/slots endpoint omits `tracts` entirely, so guard it.
    const firstTract = slot.tracts?.[0];

    // Aggregate all subtracts from all tracts for this slot
    const subtracts: SubtractNRI[] = (slot.tracts ?? []).flatMap((tract) =>
      tract.subtracts.map((subtract): SubtractNRI => {
        // Convert SlotNRITractNriFormula to TractNRIFormulaData format
        const formula = subtract.tractNriFormula;
        const tractNriFormula = formula
          ? {
              royaltyRate:
                formula.leaseRoyaltyRate ??
                (formula.royaltyRate ? parseFloat(formula.royaltyRate) : 0),
              netMineralAcres:
                formula.nma ??
                (formula.netMineralAcres
                  ? parseFloat(formula.netMineralAcres)
                  : 0),
              totalTractAcres:
                formula.grossAcres ??
                (formula.totalTractAcres
                  ? parseFloat(formula.totalTractAcres)
                  : 0),
              netRoyaltyAcres:
                formula.nra ??
                (tract.netAcreageUnits === 'nra' &&
                (formula.nma || formula.netMineralAcres) &&
                (formula.leaseRoyaltyRate || formula.royaltyRate)
                  ? ((formula.nma ??
                      parseFloat(formula.netMineralAcres ?? '0')) *
                      8) /
                    (formula.leaseRoyaltyRate ??
                      parseFloat(formula.royaltyRate ?? '0'))
                  : undefined),
              instrumentType: formula.instrumentType,
              // ORRI Burdens for OGL formula display
              orriBurdens: formula.orriBurdens ?? 0,
            }
          : null;

        const calculationMethod: 'nra' | 'nma' =
          tract.netAcreageUnits?.toLowerCase() === 'nma' ? 'nma' : 'nra';

        // Parse allocationValue (e.g. "2649.0 ft.") into number + unit,
        // fall back to gisAcres if allocationValue is not available
        const uat = subtract.unitAllocationTract;
        let allocationValue: number | undefined;
        let allocationValueUnit: string | undefined;
        if (uat?.allocationValue) {
          const match = uat.allocationValue.match(/^([\d.,]+)\s*(.*)$/);
          if (match) {
            allocationValue = parseFloat(match[1].replace(/,/g, ''));
            allocationValueUnit = match[2] || undefined;
          }
        }
        if (allocationValue === undefined && !isNil(uat?.gisAcres)) {
          allocationValue = uat.gisAcres;
          allocationValueUnit = 'ac';
        }

        return {
          nriCalculationId: subtract.nriCalculationId,
          verifications: subtract.verifications,
          crmSubtractId: subtract.name,
          crmSubtractSalesforceId: subtract.salesforceId,
          crmTractSalesforceId: tract.salesforceId,
          uat: subtract.unitAllocationTract?.tractName,
          uatNri: subtract.nri,
          uatWi: subtract.workingInterest,
          nriFormula: subtract.nriFormula,
          tractNriFormula,
          calculationMethod,
          slotName: slot.wellName ?? slot.uwi ?? undefined,
          subtractNetAcres: subtract.netAcres,
          subtractGrossAcres: subtract.grossAcres,
          quarterCall: subtract.quarterCall,
          partTractFactor: subtract.parameters.participationTractFactor,
          leaseRoyaltyRate: subtract.bundleOfSticks?.leaseRoyaltyRate,
          leaseRoyaltyType: subtract.bundleOfSticks?.leaseRoyaltyType,
          unitCoverage: subtract.parameters.unitCoverageThreshold,
          uatCoverage: subtract.parameters.uatCoverageThreshold,
          executive: subtract.bundleOfSticks?.executive,
          bonus: subtract.bundleOfSticks?.bonus,
          delay: subtract.bundleOfSticks?.delay,
          royalty: subtract.bundleOfSticks?.royalty,
          royaltyBurdens: subtract.bundleOfSticks?.royaltyBurdens,
          wi: subtract.bundleOfSticks?.wi,
          orri: subtract.bundleOfSticks?.orri,
          orriBurdens: subtract.bundleOfSticks?.orriBurdens,
          allocationValue,
          allocationValueUnit,
          allocationFactor: uat?.allocationFactor,
          geojson: subtract.geojson,
          intersectionGeojson: subtract.intersectionGeojson,
        };
      })
    );

    return {
      slotId: slot.uwi ?? slot.wellName,
      slotIdHref: slot.href ?? undefined,
      crmTract: firstTract?.name,
      crmTractSalesforceId: firstTract?.salesforceId,
      crmAccount: firstTract?.crmAccount?.name ?? '',
      crmAccountHref: firstTract?.crmAccount?.salesforceHref ?? undefined,
      unit: slot.unitTract?.name ?? '--',
      unitHref: slot.unitTract?.href,
      unitTags: slot.unitTags,
      ...slotInterestFields(slot),
      allocationMethod: slot.allocationMethod,
      tractNetAcres: firstTract?.tractNetAcres ?? firstTract?.netAcres,
      netAcreageUnits: firstTract?.netAcreageUnits,
      tractGrossAcres: firstTract?.tractGrossAcres ?? firstTract?.grossAcres,
      surveyArea: firstTract?.surveyArea,
      stateAbbreviation: slot.stateAbbreviation ?? undefined,
      quarterCall: firstTract?.quarterCall,
      instrumentType: firstTract?.instrumentType,
      interestType: firstTract?.interestType ?? slot.interestType ?? undefined,
      depthRange: firstTract?.depthRange
        ? `${format0(firstTract.depthRange.startDepth ?? 0)} - ${
            firstTract.depthRange.endDepth !== null &&
            firstTract.depthRange.endDepth !== undefined
              ? format0(firstTract.depthRange.endDepth)
              : '∞'
          }`
        : undefined,
      ...slotAttributeFields(slot),
      slotNumber: slot.slotNumber,
      ...slotDetailFields(slot),
      assetType: slot.assetType ?? null,
      targetNpvExcluded: slot.targetNpvExcluded ?? undefined,
      maxNpvExcluded: slot.maxNpvExcluded ?? undefined,
      targetNpvIncluded: slot.targetNpvIncluded ?? undefined,
      maxNpvIncluded: slot.maxNpvIncluded ?? undefined,
      titleQuality: worstSubtractRisk(
        firstTract,
        'titleQuality',
        QUALITY_SEVERITY
      ) as TitleQuality | undefined,
      titleRiskFactor: worstSubtractRisk(
        firstTract,
        'titleRiskFactor',
        RISK_SEVERITY
      ) as TitleRiskFactor | undefined,
      subtracts,
    };
  });
};
